import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Chip, Divider, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EuroIcon from '@mui/icons-material/Euro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPrices } from '../../services/storage';
import { PRICE_CATEGORIES } from './PreisePage';
import type { PriceCategory } from './PreisePage';

const MotionBox = motion(Box);

const PriceCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const location = useLocation();
  const { t } = useLanguage();
  const [pricesMap, setPricesMap] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<string | false>(false);

  const baseCategory: PriceCategory | undefined =
    (location.state as { category?: PriceCategory })?.category ??
    PRICE_CATEGORIES.find((c) => c.id === category);

  useEffect(() => {
    window.scrollTo(0, 0);
    getPrices().then(setPricesMap).catch(() => {});
    if (baseCategory) setExpanded(baseCategory.sections[0]?.id ?? false);
  }, [category]);

  if (!baseCategory) {
    return (
      <Container maxWidth="lg" sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h4">Kategorie nicht gefunden.</Typography>
        <Button component={RouterLink} to="/preise" sx={{ mt: 3 }} startIcon={<ArrowBackIcon />}>
          Zurück zu Preise
        </Button>
      </Container>
    );
  }

  const mergedSections = baseCategory.sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      price: pricesMap[item.labelKey] ?? item.price,
    })),
  }));

  const handleChange = (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 320, md: 420 },
          overflow: 'hidden',
          backgroundImage: `url(${baseCategory.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.78) 100%)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: { xs: 5, md: 7 } }}>
          <MotionBox initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Chip
              icon={<EuroIcon sx={{ fontSize: '13px !important', color: '#7b9ef0 !important' }} />}
              label={t('preise.lohn_chip')}
              sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', mb: 2.5, border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)' }}
            />
            <Typography
              variant="h1"
              sx={{ color: 'white', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.2rem' }, letterSpacing: '-1px', lineHeight: 1.1, mb: 1.5 }}
            >
              {t(baseCategory.titleKey)}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1rem', maxWidth: 480, lineHeight: 1.6 }}>
              {t(baseCategory.subtitleKey)}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ── Back link ── */}
      <Box sx={{ bgcolor: '#f8f8f8', borderBottom: '1px solid #ebebeb' }}>
        <Container maxWidth="md" sx={{ py: 1.5 }}>
          <Button
            component={RouterLink}
            to="/preise"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ color: 'text.secondary', fontSize: '0.82rem', textTransform: 'none', '&:hover': { color: '#1737bc', bgcolor: 'transparent' } }}
          >
            Zurück zu Preise
          </Button>
        </Container>
      </Box>

      {/* ── Feature List ── */}
      {baseCategory.features && baseCategory.features.length > 0 && (
        <Container maxWidth="md" sx={{ pt: { xs: 5, md: 8 }, pb: 0 }}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            sx={{ bgcolor: '#f4f6fe', borderRadius: 3, border: '1px solid #d0d5f8', p: { xs: 3, md: 4 } }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1737bc', mb: 2.5, letterSpacing: '-0.2px' }}>
              Unsere Leistungen im Überblick
            </Typography>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {baseCategory.features.map((feature, i) => (
                <Box
                  key={i}
                  component="li"
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: i < baseCategory.features!.length - 1 ? 1.75 : 0 }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#1737bc', mt: '2px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.95rem', color: '#1a2640', lineHeight: 1.55 }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </MotionBox>
        </Container>
      )}

      {/* ── Price Sections ── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        {mergedSections.map((section, idx) => (
          <MotionBox
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: idx * 0.06 }}
            viewport={{ once: true }}
          >
            <Accordion
              expanded={expanded === section.id}
              onChange={handleChange(section.id)}
              disableGutters
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: expanded === section.id ? '#1737bc' : '#e8e8e8',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                transition: 'border-color 0.25s ease',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: expanded === section.id ? '#1737bc' : 'text.secondary' }} />}
                sx={{
                  px: { xs: 2.5, md: 4 },
                  py: 2,
                  bgcolor: expanded === section.id ? '#f4f6fe' : '#fff',
                  transition: 'background-color 0.25s ease',
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 },
                }}
              >
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    bgcolor: expanded === section.id ? '#1737bc' : '#f0f0f5',
                    color: expanded === section.id ? 'white' : '#888',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.85rem', flexShrink: 0,
                    transition: 'all 0.25s ease',
                  }}
                >
                  {section.number}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.1rem' }, color: expanded === section.id ? '#1737bc' : '#1a2640', lineHeight: 1.3 }}>
                    {t(section.titleKey)}
                  </Typography>
                  {section.subtitleKey && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      {t(section.subtitleKey)}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ px: { xs: 2.5, md: 4 }, py: 0, pb: 3 }}>
                <Divider sx={{ mb: 2.5 }} />
                {section.items.map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
                      py: 1.5,
                      borderBottom: i < section.items.length - 1 ? '1px solid #f0f0f5' : 'none',
                      bgcolor: item.highlight ? '#f4f6fe' : 'transparent',
                      mx: item.highlight ? -1 : 0, px: item.highlight ? 1 : 0,
                      borderRadius: item.highlight ? 1 : 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: item.highlight ? '#1737bc' : '#aab', mt: '2px', flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.95rem', color: '#1a2640', lineHeight: 1.5 }}>
                          {t(item.labelKey)}
                        </Typography>
                        {item.noteKey && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {t(item.noteKey)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: item.highlight ? '#1737bc' : '#1a2640', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {item.price}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </MotionBox>
        ))}

        {/* Legal note */}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 4, lineHeight: 1.7, fontSize: '0.8rem' }}>
          * Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer.
        </Typography>

        {/* CTA */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          sx={{ mt: 8, p: { xs: 3, md: 5 }, bgcolor: '#f4f6fe', borderRadius: 3, border: '1px solid #d0d5f8', textAlign: 'center' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#1a2640' }}>
            {t('preise.lohn_cta_title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 420, mx: 'auto', lineHeight: 1.7 }}>
            {t('preise.lohn_cta_text')}
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            size="large"
            startIcon={<CalendarMonthIcon />}
            sx={{ bgcolor: '#1737bc', color: 'white', px: 4, py: 1.5, '&:hover': { bgcolor: '#0f2899' } }}
          >
            {t('preise.lohn_cta_btn')}
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PriceCategoryPage;
